/**
 * PreviewPanel - Step 4: Preview & Review Generated Content
 */

import React from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  AutoAwesome as AIIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { ProjectData, GenerationStatus } from '../types';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';

interface Props {
  project: ProjectData;
  generationStatus: GenerationStatus;
  onGenerate: () => void;
}

const PreviewPanel: React.FC<Props> = ({ project, generationStatus, onGenerate }) => {
  const { isAuthenticated, login } = useAuth();
  const [showAuthDialog, setShowAuthDialog] = React.useState(false);
  const [showRegister, setShowRegister] = React.useState(false);

  const handleGenerateClick = () => {
    if (!isAuthenticated) {
      setShowAuthDialog(true);
    } else {
      onGenerate();
    }
  };

  const handleLoginSuccess = (token: string, user: any) => {
    login(token, user);
    setShowAuthDialog(false);
    // After login, proceed with generation
    setTimeout(() => {
      onGenerate();
    }, 100);
  };

  const sections = [
    { id: 'executive_summary', name: 'Executive Summary', required: true },
    { id: 'context', name: 'Context & Rationale', required: true },
    { id: 'objectives', name: 'SMART Objectives', required: true },
    { id: 'methodology', name: 'Methodology & Approach', required: true },
    { id: 'work_packages', name: 'Work Packages', required: true },
    { id: 'partners', name: 'Partners & Roles', required: true },
    { id: 'quality', name: 'Quality Assurance', required: true },
    { id: 'dissemination', name: 'Dissemination Strategy', required: true },
    { id: 'budget', name: 'Budget Overview', required: true }
    // GANTT TEMPORARILY DISABLED - causes backend crashes
  ];

  const getSectionStatus = (sectionId: string) => {
    if (project.sections && project.sections[sectionId]) {
      return 'completed';
    }
    if (generationStatus.currentSection === sectionId && generationStatus.isGenerating) {
      return 'generating';
    }
    return 'pending';
  };

  const getSectionIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckIcon color="success" />;
      case 'generating':
        return <CircularProgress size={20} />;
      default:
        return <ErrorIcon color="disabled" />;
    }
  };

  const completedSections = sections.filter(s => project.sections && project.sections[s.id]).length;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom sx={{ color: 'white', fontWeight: 600 }}>
        Preview & Generate
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'white !important' }}>
        Review your input and generate the complete application document
      </Typography>

      {/* Generation Control */}
      {project.status === 'draft' && (
        <Paper sx={{ p: 3, mb: 3, textAlign: 'center', bgcolor: 'primary.light' }}>
          <Typography variant="h6" gutterBottom>
            Ready to Generate Your Application?
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            AI will create a professional 25-30 page Erasmus+ application based on your input
          </Typography>
          <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
            <strong>Please note:</strong> Generation may take several minutes. Please wait and do not close the page during the process.
          </Alert>
          <Button
            variant="contained"
            size="large"
            startIcon={<AIIcon />}
            onClick={handleGenerateClick}
            disabled={generationStatus.isGenerating}
          >
            Generate Complete Application
          </Button>
        </Paper>
      )}

      {/* Generation Progress */}
      {generationStatus.isGenerating && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.light', border: '2px solid', borderColor: 'primary.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="h6" sx={{ color: 'primary.dark', fontWeight: 600 }}>
              ⏳ Generating Content...
            </Typography>
          </Box>
          
          {/* Progress Bar */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.dark' }}>
                Progress: {generationStatus.progress}%
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {completedSections} of {sections.length} sections completed
              </Typography>
            </Box>
            <Box sx={{ width: '100%', height: 8, bgcolor: 'rgba(0, 0, 0, 0.1)', borderRadius: 4, overflow: 'hidden' }}>
              <Box
                sx={{
                  width: `${generationStatus.progress}%`,
                  height: '100%',
                  bgcolor: 'primary.main',
                  borderRadius: 4,
                  transition: 'width 0.5s ease-in-out',
                  background: 'linear-gradient(90deg, #003399 0%, #0052cc 100%)'
                }}
              />
            </Box>
          </Box>

          {/* Current Section */}
          {generationStatus.currentSection && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 255, 255, 0.7)', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.dark', mb: 0.5 }}>
                🔄 Currently generating:
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.primary', fontStyle: 'italic' }}>
                {sections.find(s => s.id === generationStatus.currentSection)?.name || generationStatus.currentSection}
              </Typography>
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
            ⏱️ Estimated time: ~4-5 minutes. Please wait and do not close this page.
          </Typography>
        </Paper>
      )}

      {/* Sections Overview */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          📋 Document Sections
        </Typography>
        {sections.map((section) => {
          const status = getSectionStatus(section.id);
          const content = project.sections?.[section.id];

          return (
            <Accordion key={section.id} disabled={status === 'pending'}>
              <AccordionSummary expandIcon={<ExpandIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  {getSectionIcon(status)}
                  <Typography sx={{ flex: 1 }}>
                    {section.name}
                  </Typography>
                  {section.required && (
                    <Chip label="Required" size="small" color="error" />
                  )}
                  {status === 'completed' && content && (
                    <Chip 
                      label={`${content.split(' ').length} words`} 
                      size="small" 
                      variant="outlined" 
                    />
                  )}
                </Box>
              </AccordionSummary>
              {content && (
                <AccordionDetails>
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {content.substring(0, 500)}
                      {content.length > 500 && '...'}
                    </Typography>
                  </Box>
                </AccordionDetails>
              )}
            </Accordion>
          );
        })}
      </Paper>

      {/* Project Summary */}
      {project.basic_info && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            📊 Project Summary
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Title
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {project.basic_info.title}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Acronym
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {project.basic_info.acronym}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Duration
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {project.basic_info.duration} months
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Budget
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                €{project.basic_info.budget.toLocaleString()}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Partners
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {project.basic_info.partners.length + 1} organisations
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Chip 
                label={project.status} 
                size="small" 
                color={project.status === 'ready' ? 'success' : 'default'}
              />
            </Box>
          </Box>
        </Paper>
      )}

      {project.status === 'ready' && (
        <Alert severity="success" sx={{ mt: 3 }}>
          ✅ Your application is ready! Proceed to the next step to export as PDF.
        </Alert>
      )}

      {/* Authentication Dialog */}
      <Dialog
        open={showAuthDialog}
        onClose={() => setShowAuthDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          Authentication Required
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {showRegister ? (
            <RegisterForm
              onRegisterSuccess={handleLoginSuccess}
              onSwitchToLogin={() => setShowRegister(false)}
              compact={true}
            />
          ) : (
            <LoginForm
              onLoginSuccess={handleLoginSuccess}
              onSwitchToRegister={() => setShowRegister(true)}
              compact={true}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PreviewPanel;

